import type { MutableToolStep, MutableToolStepKey } from '@toolproof-core/lib/_contract/workflow-overlay';

import { deriveToolStepBounds, upsertMacroStepBounds } from '@/workflow/_pure/derivation/macroStepBounds';
import { deriveCaseMembership } from '@/workflow-builder/_pure/derivation/caseMembership';
import type { MacroStepConnectorSeed } from '@/workflow/_contract/kernel';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';

export function deriveMacroStepConnectorSeeds(args: {
    runtimeState: RuntimeState;
    projectedToolSteps: readonly MutableToolStep[];
}): MacroStepConnectorSeed[] {
    const boundsByMacroStepKey = new Map<string, {
        minStepIndex: number;
        maxStepIndex: number;
        minStepKey: MutableToolStepKey;
        maxStepKey: MutableToolStepKey;
    }>();
    const kindByMacroStepKey = new Map<string, MacroStepConnectorSeed['macroStepKind']>();
    for (const membership of deriveCaseMembership(args)) {
        kindByMacroStepKey.set(membership.ownerMacroStepKey, membership.slotFamily === 'cases' ? 'branch' : 'loop');
        const stepIndexByStepKey = new Map([
            [membership.whenToolStepKey, membership.whenStepIndex],
            [membership.whatToolStepKey, membership.whatStepIndex],
        ]);
        const bounds = deriveToolStepBounds({
            aToolStepKey: membership.whenToolStepKey,
            bToolStepKey: membership.whatToolStepKey,
            stepIndexByStepKey,
        });
        if (!bounds) continue;
        upsertMacroStepBounds({
            boundsByMacroStepKey, macroStepKey: membership.ownerMacroStepKey, nextBounds: bounds,
        });
    }
    return Array.from(boundsByMacroStepKey, ([logicalMacroStepKey, bounds]) => ({
        macroStepKind: kindByMacroStepKey.get(logicalMacroStepKey)!,
        logicalMacroStepKey,
        startToolStepKey: bounds.minStepKey,
        endToolStepKey: bounds.maxStepKey,
        startStepIndex: bounds.minStepIndex,
        endStepIndex: bounds.maxStepIndex,
    }));
}
